/// <reference types="@types/googlemaps" />

import { Injectable } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import {
  HTTPWrapperContext,
  RequestConfigImplementation,
  REQUEST_METHODS,
  XHR_STRATEGIES
} from '@ptl/xhr';
import { PlansStateStructure } from 'src/app/angular-elements/plan-selection/configs/interfaces/plansStateStructure.interface';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { PlanAttributes, SummaryView, MedicareAdvantage_and_PDP_PlanAttributes } from 'src/app/angular-elements/plan-selection/interfaces/plan-attributes.interface';
import { Product } from 'src/app/angular-elements/plan-selection/interfaces/product.interface';
import { PRODUCT_GROUPS } from 'src/app/angular-elements/plan-selection/enums/product-groups.enum';
import { SummaryResponse } from '../configs/interfaces/provider-lookup/response/summaryResponse.interface';
import { DOCTOR_CONTENT_OVERRIDE_NAME, DoctorContentOverride, ProviderLookupAutoCompleteResponse } from '../configs/interfaces/provider-lookup/response/response.interface';
import { AcceptedNetworksResponse, OtherLocationsResponse } from '../configs/interfaces/doctors.interface';
import { PlanUtilityService } from 'src/app/angular-elements/plan-selection/plan-summary-list/plan-utility.service';
import { Brand, BrandingConfigurationService, brandNENY, brandWNY } from '@ptl/brand';
import { ZipCountyStateStructure } from '../../../shared/interfaces/zipCountyStateStructure.interface';
import { LINE_OF_BUSINESS_ENUM } from '@ptl/account-management';
import { ContentStorageService } from '@ptl/global-components';

@Injectable({
  providedIn: 'root'
})
export class DoctorInformationService {
  constructor(
    private httpClient: HTTPWrapperContext,
    private reducerUtilityService: ReducerUtilityService,
    private planUtilityService: PlanUtilityService,
    private brandService: BrandingConfigurationService,
    private contentService: ContentStorageService
  ) { }

  // TODO: This should be sent to a brand aware service in case we use a different brand/ wy/mn/nd!
  // brandCode = 'mhmbcbs';


  byDocNameAutoComplete(
    market: string,
    searchString: string,
    radius: number,
    latitude: number = 40.4475255,
    longitude: number = -79.9933976,
    primaryCareProviderOnly: boolean,
    useBrandNetwork: boolean
  ): Promise<ProviderLookupAutoCompleteResponse> {
    /**
     * @TODO
     * Configuration to saphfire digital versus watson provider search.
     */
    const overrides = this.contentService.getContentMap(DOCTOR_CONTENT_OVERRIDE_NAME);
    const queryParamOverride = overrides.get('queryParams');
    const limit = 20;

    let urlString = `/dpsext/x-services/SapphireCareSelectAPIs/api/providers/autocomplete.json?` +
      `geo_location=${latitude},${longitude}` +
      `&limit=${limit}` +
      `&name=${searchString}` +
      `&radius=${radius}` +
      `sort=score desc, distance asc`;

    /** If PCP flow, and plan is MEDICARE ADVANTAGE or PRESCRIPTION DRUG PLAN, do provider network query param */
    // we can override the default query params with the overrides content option
    if (primaryCareProviderOnly) {
      urlString += this.getPrimaryCareProviderUrlQueryParams(queryParamOverride);
    }
    // Return the overrided value as is if it exists
    else if (queryParamOverride) {
      urlString += queryParamOverride;
    }
    // Use special branded network query params
    else if (useBrandNetwork){
      urlString += this.getBrandUrlQueryParams(market);
    }

    const requestConfigImplementation = new RequestConfigImplementation({
      method: REQUEST_METHODS.GET,
      url: urlString,
      headers: new HttpHeaders({
        ddToken: this.brandService.getCurrentBrand().ddToken_encoded()
      })
    });
    this.httpClient.setStrategy(XHR_STRATEGIES.DATAPOWER_MIDDLEWARE);
    return this.httpClient.makeRequest(requestConfigImplementation);
  }

  private getBrandUrlQueryParams(market: string) {
    // Capture the current brand and market constants and apply them to query params
    let urlString = '';
    let brandNetworkConstants = this.getBrandedQueryParamConstants(market);

    if (brandNetworkConstants.docSuperNetwork) {
      urlString += `&network_id=${brandNetworkConstants.docSuperNetwork}`;
    }
    if (brandNetworkConstants.docConfigSignature) {
      urlString += `&config_signature=${brandNetworkConstants.docConfigSignature}`;
    }

    return urlString;
  }

  private getPrimaryCareProviderUrlQueryParams(queryParamOverride: string) {
    let urlString = `&is_pcp=Y`;
    let networkId = 177;

    const planState: PlansStateStructure = this.reducerUtilityService.getState('Plans');
    const plans: Product<PlanAttributes, SummaryView>[] = this.planUtilityService.getPlansByProductGroupAsArray();
    const matchedPlans = plans.filter(plan => plan.attributes.PLAN_ID === planState.planIdForEnrollment);

    let selectedPlan: Product<PlanAttributes, SummaryView> = null;
    if (matchedPlans && matchedPlans.length > 0) {
      selectedPlan = matchedPlans[0];
    }

    if (selectedPlan
      && (selectedPlan.attributes.PRODUCT_GROUP.toUpperCase() === PRODUCT_GROUPS.MEDICARE_ADVANTAGE.toUpperCase()
        || selectedPlan.attributes.PRODUCT_GROUP.toUpperCase() === PRODUCT_GROUPS.PRESCRIPTION_DRUG_PLAN.toUpperCase())) {
      const attributes = selectedPlan.attributes as MedicareAdvantage_and_PDP_PlanAttributes;
      if (attributes.PROVIDER_NETWORK_ID) {
        urlString += `&network_id=${attributes.PROVIDER_NETWORK_ID}`;
      } 
      else {
        // Apply the override query params first if we could not determine a network id from the plan
        if (queryParamOverride) {
          urlString += queryParamOverride;
        }
        else {
          urlString += `&network_id=${networkId}`;
        }
      }
    }

    return urlString;
  }

  private getBrandedQueryParamConstants(market: string) {
    const zipCountyState: ZipCountyStateStructure = this.reducerUtilityService.getState('ZipCounty');
    let curBrand = this.brandService.getBrandFromCookieCode(zipCountyState.county.brand) as Brand;
    
    if (market?.toUpperCase() === LINE_OF_BUSINESS_ENUM.SENIOR) {
      return curBrand.seniorMarketConstants;
    }
    else {
      return curBrand.individualMarketConstants;
    }
  }

  getAcceptedNetwork(providerId: string, locationId: string): Promise<AcceptedNetworksResponse> {
    const urlString
      = `/dpsext/x-services/SapphireCareSelectAPIs/api/providers/${providerId}/locations/${locationId}/networks_accepted.json`;

    const requestConfigImplementation = new RequestConfigImplementation({
      method: REQUEST_METHODS.GET,
      url: urlString,
      headers: new HttpHeaders({
        ddToken: this.brandService.getCurrentBrand().ddToken_encoded()
      })
    });

    this.applyNYQueryParam(requestConfigImplementation);

    this.httpClient.setStrategy(XHR_STRATEGIES.DATAPOWER_MIDDLEWARE);
    return this.httpClient.makeRequest(requestConfigImplementation);
  }

  getProviderLocation(providerId: string, locationId: string): Promise<SummaryResponse> {
    const urlString
      = `/dpsext/x-services/SapphireCareSelectAPIs/api/providers/${providerId}/locations/${locationId}/summary.json`;

    const requestConfigImplementation = new RequestConfigImplementation({
      method: REQUEST_METHODS.GET,
      url: urlString,
      headers: new HttpHeaders({
        ddToken: this.brandService.getCurrentBrand().ddToken_encoded()
      })
    });

    this.applyNYQueryParam(requestConfigImplementation);

    this.httpClient.setStrategy(XHR_STRATEGIES.DATAPOWER_MIDDLEWARE);
    return this.httpClient.makeRequest(requestConfigImplementation);
  }

  getOtherLocations(providerId: string, locationId: string): Promise<OtherLocationsResponse> {
    const urlString
      = `/dpsext/x-services/SapphireCareSelectAPIs/api/providers/${providerId}/locations/${locationId}/other_locations.json`;

    const requestConfigImplementation = new RequestConfigImplementation({
      method: REQUEST_METHODS.GET,
      url: urlString,
      headers: new HttpHeaders({
        ddToken: this.brandService.getCurrentBrand().ddToken_encoded()
      })
    });

    this.httpClient.setStrategy(XHR_STRATEGIES.DATAPOWER_MIDDLEWARE);
    return this.httpClient.makeRequest(requestConfigImplementation);
  }

  private applyNYQueryParam(requestConfigImplementation: RequestConfigImplementation) {
    // NY specific query param needs added
    const zipState: ZipCountyStateStructure = this.reducerUtilityService.getState('ZipCounty');
    if (zipState.county.region === brandNENY.region) {
      requestConfigImplementation.url += '?config_signature=%7B753%7D-%7B%7D-%7B%7D';
    }
    if (zipState.county.region === brandWNY.region) {
      requestConfigImplementation.url += '?config_signature=%7B752%7D-%7B%7D-%7B%7D';
    }
  }

  private getPlanNetworkId() {
      const networkId = 177;
      const planState: PlansStateStructure = this.reducerUtilityService.getState('Plans');

      const plans: Product<PlanAttributes, SummaryView>[] = this.planUtilityService.getPlansByProductGroupAsArray();

      const matchedPlans = plans.filter(plan => plan.attributes.PLAN_ID === planState.planIdForEnrollment);

      let selectedPlan: Product<PlanAttributes, SummaryView> = null;
      if (matchedPlans && matchedPlans.length > 0) {
        selectedPlan = matchedPlans[0];
      }

      if (selectedPlan
        && (selectedPlan.attributes.PRODUCT_GROUP.toUpperCase() === PRODUCT_GROUPS.MEDICARE_ADVANTAGE.toUpperCase()
          || selectedPlan.attributes.PRODUCT_GROUP.toUpperCase() === PRODUCT_GROUPS.PRESCRIPTION_DRUG_PLAN.toUpperCase())) {
        const attributes = selectedPlan.attributes as MedicareAdvantage_and_PDP_PlanAttributes;
        if (attributes.PROVIDER_NETWORK_ID) {
          return `&network_id=${attributes.PROVIDER_NETWORK_ID}`;
        } else {
          return `&network_id=${networkId}`;
        }
      }

  }

  getIdentifiers(
    providerId: string,
    locationId: string,
    latitude: number = 40.4475255,
    longitude: number = -79.9933976,
    primaryCareProviderOnly?: boolean
    ): Promise<any> {
    let urlString
      = `/dpsext/x-services/SapphireCareSelectAPIs/api/providers/${providerId}/locations/${locationId}/identifiers.json/?provider_id=${providerId}&location_id=${locationId}`;

    if (primaryCareProviderOnly) {
      urlString = `${urlString}${this.getPlanNetworkId()}&geo_location=${latitude},${longitude}&locale=en_us&ci=home&data_language=en`
    }
    const requestConfigImplementation = new RequestConfigImplementation({
      method: REQUEST_METHODS.GET,
      url: urlString,
      headers: new HttpHeaders({
        ddToken: this.brandService.getCurrentBrand().ddToken_encoded()
      })
    });

    this.httpClient.setStrategy(XHR_STRATEGIES.DATAPOWER_MIDDLEWARE);
    return this.httpClient.makeRequest(requestConfigImplementation);
  }
}

